import { ChangeDetectionStrategy, Component, inject, signal } from "@angular/core";
import { NgTemplateOutlet } from "@angular/common";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { TranslatePipe } from "@ngx-translate/core";
import { JumpstartComponentsModule } from "@wk/components-v3-angular17";
import { type NotificationContent, type NotificationModel } from "./notification.model";
import { DashboardsService } from "../shared/dashboards-service/dashboards.service";
import { ResponseErrorHandlerService } from "../shared/response-error-handler-service/response-error-handler.service";
import { DEFAULT_HEADINGS } from "./notification.catalog";

@Component({
    selector: "wk-notification",
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: "./notification.component.html",
    imports: [JumpstartComponentsModule, TranslatePipe, NgTemplateOutlet],
})
export class NotificationComponent {
    private readonly dashboardsService = inject(DashboardsService);
    private readonly responseErrorHandlerService = inject(ResponseErrorHandlerService);
    protected readonly DEFAULT_HEADINGS = DEFAULT_HEADINGS;

    readonly notifications = signal<readonly NotificationModel[]>([]);
    private nextNotificationId = 0;

    constructor() {
        this.responseErrorHandlerService.showNotificationEvent$.pipe(takeUntilDestroyed()).subscribe((definition) => {
            const activeNotification = this.notifications().find((notification) => notification.isOpen);
            const notification: NotificationModel = {
                ...definition,
                autoCloseTimeoutMs: definition.autoCloseTimeoutMs ?? null,
                id: activeNotification?.id ?? ++this.nextNotificationId,
                isOpen: true,
            };
            this.notifications.set([notification]);
        });

        this.dashboardsService.dashboardChangeEvent$.pipe(takeUntilDestroyed()).subscribe(() => this.closeAllNotifications());
    }

    protected closeNotification(id: number): void {
        this.notifications.update((notifications) =>
            notifications.map((notification) => (notification.id === id ? { ...notification, isOpen: false } : notification)),
        );
    }

    private closeAllNotifications(): void {
        this.notifications.update((notifications) => notifications.map((notification) => ({ ...notification, isOpen: false })));
    }

    protected isMultipartContent(content: NotificationModel["content"]): content is NotificationContent[] {
        return Array.isArray(content);
    }
}
